import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Badge,
  Tooltip,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import InfoIcon from "@mui/icons-material/Info";
import ExtensionIcon from "@mui/icons-material/Extension";
import PeopleIcon from "@mui/icons-material/People";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import axios from "axios";

/* ---------- Helper ---------- */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString; // fallback if parsing fails
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
/* ---------------------------- */

const UserInfoPopup = ({ user, usersData, onClose, onEnableModule, token }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [userModules, setUserModules] = useState([]);
  const [adminData, setAdminData] = useState([]);

  /* -------- Fetch admin details on mount -------- */
  useEffect(() => {
    const fetchAdminDetails = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/shraddha/operation/adminDetails`,
          { id: user.admin_Id },
          {
            headers: { Authorization: token },
          }
        );

        setAdminData(response.data.AdminMap["0"].Admin[0]);
       console.log("vilas", response.data.AdminMap["0"].Admin[0]);
      } catch (error) {
        console.error("Error fetching admin details:", error);
        // No static fallback; UI will rely on existing props
      }
    };

    if (user?.admin_Id) fetchAdminDetails();
  }, [token, user?.admin_Id]);
  /* ---------------------------------------------- */

  const handleTabChange = (_evt, newValue) => setTabValue(newValue);

  /* ---------- Derived helpers ---------- */

  const getUserCompanies = () => {
    if (adminData) {
      return adminData.Company.map((c) => c.CmpName);
    }
    return user
      ? usersData
          .filter((u) => u.admin_Email === user.admin_Email)
          .map((u) => u.admin_CmpName)
          .filter((v, i, self) => self.indexOf(v) === i)
      : [];
  };

  const getUserModules = async (userId) => {
    try {
      if (adminData) {
        return adminData.Module.map((module) => ({
          module_Id: module.moduleId,
          module_Name: module.moduleName,
          module_Purchased: module.modulePurchased,
          module_Expiry: module.moduleExpiry,
          module_Clicks: module.moduleClicks,
        }));
      }
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/shraddha/module/mymodule`,
        { id: userId },
        { headers: { Authorization: token } }
      );
      //  console.log(data)
      return Object.values(data);
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getAssignedUsers = () => {
    if (adminData) {
      return adminData.User.map((u) => ({
        admin_Id: u.userId,
        admin_FirstName: u.userName.split(" ")[0] || "",
        admin_LastName: u.userName.split(" ")[1] || "",
        admin_Email: u.userEmail,
        admin_Mobile: "N/A",
        admin_last_Login: null,
        admin_CmpName: adminData.adminCmpName,
        userRole: u.userRole,
      }));
    }
    return user
      ? usersData.filter(
          (u) =>
            u.admin_CmpName === user.admin_CmpName &&
            u.admin_Id !== user.admin_Id
        )
      : [];
  };
  /* ------------------------------------- */

  /* -------- Fetch user modules when user/adminData ready -------- */
  useEffect(() => {
    if (user) {
      getUserModules(user.admin_Id).then(setUserModules);
    }
  }, [user, adminData]);
  /* -------------------------------------------------------------- */

  if (!user) return null;


  function formatIST(isoDate) {
    if (!isoDate) return "N/A"; // जर date null/undefined असेल तर
    const date = new Date(isoDate);
  
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  }

  return (
    <Card
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 1300,
        overflow: "auto",
        borderRadius: 0,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardHeader
        title={
          <Typography variant="h3" sx={{ fontWeight: "bold" }}>
            User Details
          </Typography>
        }
        action={
          <IconButton
            onClick={onClose}
            sx={{
              color: "white", //theme.palette.text.secondary,
              marginRight: 3,
              backgroundColor: "black",
              "&:hover": { color: theme.palette.error.main },
            }}
          >
            <CloseIcon />
          </IconButton>
        }
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
        }}
      />

      <CardContent>
        {/* ---------- Header block ---------- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 4,
            [theme.breakpoints.down("sm")]: { flexDirection: "column" },
          }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Tooltip title="Active User">
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "#44b700",
                    border: `2px solid ${theme.palette.background.paper}`,
                  }}
                />
              </Tooltip>
            }
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                fontSize: "2rem",
                backgroundColor: theme.palette.primary.main,
              }}
            >
              {user.admin_FirstName[0]}
              {user.admin_LastName[0]}
            </Avatar>
          </Badge>

          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {user.admin_FirstName} {user.admin_LastName}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {user.admin_Email}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                icon={<BusinessIcon fontSize="small" />}
                label={user.admin_CmpName}
                size="small"
                variant="outlined"
              />
              {user.member_From && (
                <Chip
                  icon={<EventIcon fontSize="small" />}
                  label={`Member since ${formatDate(user.member_From)}`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </Box>
        </Box>
        {/* ----------------------------------- */}

        {/* ---------- Tabs ---------- */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, "& .MuiTabs-indicator": { height: 3 } }}
        >
          <Tab
            label="Overview"
            icon={<InfoIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label="Companies"
            icon={<BusinessIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label="Modules"
            icon={<ExtensionIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label="Team"
            icon={<PeopleIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
        {/* ------------------------- */}

        {/* ---------- Overview tab ---------- */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <EmailIcon color="action" sx={{ mr: 2 }} />
                  <Typography>{user.admin_Email}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PhoneIcon color="action" sx={{ mr: 2 }} />
                  <Typography>{user.admin_Mobile}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BusinessIcon color="action" sx={{ mr: 2 }} />
                  <Typography>{user.admin_CmpName}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EventIcon color="action" sx={{ mr: 2 }} />
                  <Typography>
                  Last Login: {formatIST(adminData.adminlastLogin)}
                    
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {user.member_From && user.member_To && (
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Membership Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EventIcon color="action" sx={{ mr: 2 }} />
                    <Typography>
                      <strong>From:</strong> {formatDate(user.member_From)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <EventIcon color="action" sx={{ mr: 2 }} />
                    <Typography>
                      <strong>To:</strong> {formatDate(user.member_To)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" , mt: 2  }}>
                    <ConfirmationNumberIcon color="action" sx={{ mr: 2 }} />
                    <Typography>
                      <strong>Tally Serial Number:</strong> {adminData.tallySerialNo}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" , mt: 2  }}>
                    <AllInclusiveIcon color="action" sx={{ mr: 2 }} />
                    <Typography>
                      <strong>Infinite Serial Number:</strong> {adminData.infiniteSerialNo}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
        {/* ---------------------------------- */}

        {/* ---------- Companies tab ---------- */}
        {tabValue === 1 && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: "bold" }}>
              Associated Companies
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {getUserCompanies().length ? (
              <List sx={{ maxHeight: 300, overflow: "auto" }}>
                {getUserCompanies().map((cmp, i) => (
                  <ListItem
                    key={i}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: "medium" }}>
                          {cmp}
                        </Typography>
                      }
                      secondary={`Company ${i + 1}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  color: theme.palette.text.secondary,
                }}
              >
                <BusinessIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography>
                  No other companies associated with this user
                </Typography>
              </Box>
            )}
          </Paper>
        )}
        {/* ----------------------------------- */}

        {/* ---------- Modules tab ---------- */}
        {tabValue === 2 && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: "bold" }}>
              Assigned Modules
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {userModules.length ? (
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: theme.palette.action.hover }}
                    >
                      <TableCell sx={{ fontWeight: "bold" }}>Module</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Purchased
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Expiry</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userModules.map((m) => {
                      const expiry = m.module_Expiry
                        ? new Date(m.module_Expiry)
                        : null;
                      const isExpired = expiry && expiry < new Date();
                      return (
                        <TableRow
                          key={m.module_Id}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell>{m.module_Name}</TableCell>
                          <TableCell>
                            {formatDate(m.module_Purchased)}
                          </TableCell>
                          <TableCell>
                            {m.module_Expiry
                              ? formatDate(m.module_Expiry)
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isExpired ? "Expired" : "Active"}
                              size="small"
                              color={isExpired ? "error" : "success"}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  color: theme.palette.text.secondary,
                }}
              >
                <ExtensionIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography>No modules assigned to this user</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    onClose();
                    onEnableModule({ id: user.admin_Id });
                  }}
                >
                  Assign Modules
                </Button>
              </Box>
            )}
          </Paper>
        )}
        {/* ----------------------------------- */}

        {/* ---------- Team tab ---------- */}
        {tabValue === 3 && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: "bold" }}>
              Team Members
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {getAssignedUsers().length ? (
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: theme.palette.action.hover }}
                    >
                      <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                      {/* <TableCell sx={{ fontWeight: "bold" }}>Last Login</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAssignedUsers().map((u) => (
                      <TableRow
                        key={u.admin_Id}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                mr: 2,
                                fontSize: "0.875rem",
                                backgroundColor: theme.palette.secondary.main,
                              }}
                            >
                              {u.admin_FirstName[0]}
                              {u.admin_LastName[0]}
                            </Avatar>
                            {u.admin_FirstName} {u.admin_LastName}
                          </Box>
                        </TableCell>
                        <TableCell>{u.admin_Email}</TableCell>
                        {/* <TableCell>
                          {u.admin_last_Login
                            ? formatDate(u.admin_last_Login)
                            : "Never logged in"}
                        </TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  color: theme.palette.text.secondary,
                }}
              >
                <PeopleIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography>
                  No other users found in the same company
                </Typography>
              </Box>
            )}
          </Paper>
        )}
        {/* ---------------------------------- */}
      </CardContent>
    </Card>
  );
};

export default UserInfoPopup;
